// Local capability resolution for the UoM (S5) + Templates (S8) screens
// (Module 01 §7). The write surfaces are hidden for roles that cannot perform
// them: UoM + template management need `catalogue.uom.manage` /
// `catalogue.template.manage` (group_admin, facility_admin); the template "Use"
// deep-links open authoring create flows, open to planner as well.
//
// The server is the authoritative gate (a hidden control is a convenience, not a
// security boundary — every mutation is still authorised API-side). Each cap
// reads the permission code its own write rides (D-586), so a tenant's clone of
// an authoring role is offered what it holds and a trimmed one is offered less.

import { type RoleInput, useRoleCodesFeed } from "../../../../../lib/role-codes";
import { deriveCataloguePermissions } from "../../hooks/permissions";

export interface CatalogueCaps {
  /** May create/edit/archive units + conversions (`catalogue.uom.manage`). */
  canManageUoms: boolean;
  /** May save/archive templates (`catalogue.template.manage`). */
  canManageTemplates: boolean;
  /** May open a template into a create flow (authoring roles incl. planner). */
  canUseTemplates: boolean;
}

/**
 * Pure caps derivation (unit-tested). Managing units and templates rides
 * their own codes; "Use" opens a product, BOM or routing create flow, so it
 * follows the structure-authoring codes, which group_admin, facility_admin
 * and planner hold among the seed roles.
 */
export function deriveCaps(roles: RoleInput): CatalogueCaps {
  const codes = deriveCataloguePermissions(roles);
  return {
    canManageUoms: codes.has("catalogue.uom.manage"),
    canManageTemplates: codes.has("catalogue.template.manage"),
    canUseTemplates: codes.has("catalogue.bom.write") || codes.has("catalogue.routing.write"),
  };
}

/**
 * Caps for the current caller. `isResolved` is false until the role feed
 * settles — write controls stay hidden until then (never flashed to a role that
 * cannot use them). If the feed fails, caps resolve to none (fail-closed): the
 * screens degrade to read-only rather than showing controls a role may lack.
 */
export function useCatalogueCaps(): CatalogueCaps & { isResolved: boolean } {
  const feed = useRoleCodesFeed();
  return { isResolved: feed.isResolved, ...deriveCaps(feed) };
}
